/**
 * Pure helpers for deriving Blitz (Phase 3) state on the `/blitz/:projectId`
 * route. Kept dependency-free so they can be unit-tested without mocking axios
 * or React.
 *
 * Status vocabulary (v0.12.x):
 *   - aggregate blitz_state / audio_state: not_started | in_progress | done | failed
 *   - per-file blitz_status: not_started | pending | running | done | failed
 *     pending = queued by auto-blitz or an explicit trigger, not yet picked up
 */

export const isPhaseDone = (s) => s === 'done' || s === 'complete';
export const isPhaseActive = (s) => s === 'in_progress' || s === 'pending' || s === 'running';

const anyFileBlitzActive = (files) =>
  Array.isArray(files) && files.some((f) => f.blitz_status === 'running' || f.blitz_status === 'pending');

// Phase 3 is "done" when the polled aggregate says so OR every member file
// reports blitz_status 'done' (the latter is available from GET /projects/{id}
// before the first poll resolves and survives a page refresh).
export function deriveBlitzDone(blitzState, projectFiles) {
  if (isPhaseDone(blitzState)) return true;
  const files = Array.isArray(projectFiles) ? projectFiles : [];
  return files.length > 0 && files.every((f) => f.blitz_status === 'done');
}

// Phase 3 is "in flight" when the aggregate or any file reports an active blitz
// (pending or running). The old autoPhase3 fallback is no longer needed: when
// blitz is queued automatically the backend sets blitz_status to 'pending'.
export function deriveBlitzInFlight({ phase3IsDone, blitzState, projectFiles, snapshotFiles }) {
  if (phase3IsDone) return false;
  if (isPhaseActive(blitzState)) return true;
  if (anyFileBlitzActive(projectFiles) || anyFileBlitzActive(snapshotFiles)) return true;
  return false;
}
